// filepath: apps/web/src/design-system/error-states/InlineFieldError.tsx
//
// FR-UI-CORE-10 AC-5 — Inline per-field validation error primitive.
//
// Renders a single validation error message next to a form field. Used
// by the FormRenderer's per-field slot when the server returns HTTP 422
// with a per-field error envelope (see useFormSubmit.ts HttpValidationError
// and apps/web/src/definitions/types.ts FieldErrorMap).
//
// INVARIANTS:
//   - ARIA wiring (FR-UI-CORE-9): role="alert" + aria-live="polite" so
//     screen readers announce the error when it appears. The id
//     attribute on the wrapper is the SAME id the field input carries
//     in its aria-describedby attribute — that pairing is what makes
//     the field+error pair discoverable as a unit.
//   - INV-9: the message is rendered as a text node. No
//     dangerouslySetInnerHTML, no HTML interpretation.
//   - The chrome label (aria-label) is localized via the
//     'validation.error.inline' i18n key (ru/kk/en per FR-UI-CORE-8).
//     The message body is whatever the server supplied — the chrome
//     label is the only SPA-authored string.
//   - The component is intentionally minimal: a wrapper, a label, a
//     message. Field components own their input + label rendering;
//     this component owns the error surface only.
//
// The component is rendered as a SIBLING of the field input element
// inside the field's wrapper div, so screen readers see:
//     <div class="field field-string">
//       <label>...</label>
//       <input aria-invalid="true" aria-describedby="email-error" />
//       <InlineFieldError id="email-error" message="..." />
//     </div>
// (the wrapping div is provided by the field component).

import React from "react";
import { useIsMobile } from "@/runtime/useIsMobile";
import { useTranslation } from "@/i18n/useTranslation";

export interface InlineFieldErrorProps {
  /**
   * The DOM id used by the field input's `aria-describedby` attribute.
   * MUST be unique per field on the form. The FormRenderer produces
   * this as `${fieldId}-error`.
   */
  id: string;
  /**
   * The localized error message from the server's
   * `{errors: {fieldId: {message: string | {locale: value}}}}` envelope.
   * Plain strings are accepted as-is; locale maps are resolved to the
   * active locale by the caller (FormRenderer) BEFORE passing here.
   */
  message: string;
  /**
   * Optional machine-readable error code. Used by tests and by any
   * future analytics / telemetry that wants to bucket errors. Not
   * rendered to the user.
   */
  code?: string;
}

/**
 * InlineFieldError — the FR-UI-CORE-10 AC-5 primitive.
 *
 * Used in two places:
 *   1. As a SIBLING of the field input (the field component renders
 *      its input + this component underneath, both inside the same
 *      `<div class="field ...">` wrapper).
 *   2. By the FormRenderer per-field slot to wire `aria-describedby`
 *      on the field input to the matching error element's id.
 *
 * @example
 *   <input id="email" aria-describedby="email-error" />
 *   <InlineFieldError id="email-error" message="Email is required" />
 */
export function InlineFieldError({
  id,
  message,
  code,
}: InlineFieldErrorProps): React.ReactElement | null {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // The aria-label gives screen readers context ("Validation error")
  // before they read the message body. The visible message itself is
  // the localized error string from the server.
  const label = t("validation.error.inline", "Validation error");
  // Empty messages render as null — defensive guard so a server that
  // accidentally returns `{message: ""}` doesn't produce an empty
  // alert that screen readers would announce as a sound-only ping.
  if (message === "") {
    return null;
  }
  return (
    <p
      id={id}
      data-testid={id}
      data-field-error-code={code}
      className="field-error"
      role="alert"
      aria-live="polite"
      aria-label={label}
      style={
        isMobile ? { wordBreak: "break-word", overflowWrap: "break-word" } : {}
      }
    >
      {message}
    </p>
  );
}
